//* 绘图库
import Konva from "konva";
//* 组件属性
import type { GeneticInformationOrigin } from "../../index";

//* 配置类型
interface Setting {
    size: number;
    spacing: number;
    chromosome: number;
    attachments: number;
}

export const render = (origin: GeneticInformationOrigin, stage: Konva.Stage, setting: Setting) => {
    //* 初始化舞台
    const canvas = new Konva.Layer();

    //* 染色体数据初始化
    const chromosomes = origin.chromosome.data.sort((a, b) => a.start - b.start);
    //* 染色体标注位置初始化
    const chromosome_note = origin.chromosome.data.find((value) => value.end >= origin.chromosome.start && value.start <= origin.chromosome.start);
    //* 染色体数据分组
    const bands_p = <any[]>[];
    const bands_q = <any[]>[];
    chromosomes.forEach((value) => {
        if (value.id.includes("p")) bands_p.push(value);
        else if (value.id.includes("q")) {
            bands_q.push(value);
        }
    });

    //* 数据初始化
    const config = { spacing: 32, offset: 0, ratio: 0 };
    const config_p = {
        size: bands_p.length ? bands_p.slice(-1)[0]?.end - bands_p[0].start + 1 : 0,
        begin: bands_p?.[0]?.start || 0,
        content: 2,
        wireframe: 1,
    };
    const config_q = {
        size: bands_q.length ? bands_q.slice(-1)[0]?.end - bands_q[0].start + 1 : 0,
        begin: bands_q?.[0]?.start || 0,
        content: 2,
        wireframe: 1,
    };

    //* 实例化标注的文本对象
    const note = new Konva.Text({ text: chromosome_note?.id, fontFamily: "sans-serif", fontStyle: "bold", fontSize: 14 });
    if (chromosome_note) {
        config.offset = Math.ceil(note.getTextWidth() + 14);
    }

    //* 转换比例初始化
    config.ratio = (setting.size - config.spacing) / (config_p.size + config_q.size);

    //* 数据处理
    if (bands_p.length && bands_q.length) {
        config.spacing = 64;
        config.ratio = (setting.size - config.spacing) / (config_p.size + config_q.size);
        config_q.wireframe = config_p.size * config.ratio + 33;
        config_q.content = config_p.size * config.ratio + 34;
    }

    //* 内容绘制函数
    const content = (minus: boolean, data: any[], option: { size: number; begin: number; content: number; wireframe: number }) => {
        //* 实例化结果
        const result = new Konva.Group();
        //* 检测是否需要绘制
        if (!option.size) return result;
        else {
            //* 数据初始化
            const radius = minus ? [24, 24, 4, 4] : [4, 4, 24, 24];
            //* 实例化内容
            const content = new Konva.Group({
                x: config.offset + 2,
                y: option.content,
                clipFunc: (ctx) => {
                    const x = -2; //* 矩形的起始X坐标
                    const y = -2; //* 矩形的起始Y坐标
                    const width = 48; //* 矩形的宽度
                    const height = option.size * config.ratio + 4; //* 矩形的高度
                    const radius1 = 24;
                    const radius2 = 4;

                    //* 路径开启
                    ctx.beginPath();

                    //* 绘制
                    if (minus) {
                        ctx.moveTo(x, y + radius1);
                        ctx.quadraticCurveTo(x + 1, y + 1, x + width / 2, y);
                        ctx.quadraticCurveTo(x + width - 1, y + 1, x + width, y + radius1);
                        ctx.lineTo(x + width, y + height + radius1);
                        ctx.quadraticCurveTo(x + width, y + height + radius1 + radius2, x + width - radius2, y + height + radius1 + radius2);
                        ctx.lineTo(x + radius2, y + height + radius1 + radius2);
                        ctx.quadraticCurveTo(x, y + height + radius1 + radius2, x, y + height + radius1);
                    } else {
                        ctx.moveTo(x, y + radius2);
                        ctx.quadraticCurveTo(x, y, x + radius2, y);
                        ctx.lineTo(x + width - radius2, y);
                        ctx.quadraticCurveTo(x + width, y, x + width, y + radius2);
                        ctx.lineTo(x + width, y + height + radius2);
                        ctx.quadraticCurveTo(x + width - 1, y + height + radius1 + radius2 - 1, x + width / 2, y + height + radius1 + radius2);
                        ctx.quadraticCurveTo(x + 1, y + height + radius1 + radius2 - 1, x, y + height + radius2);
                    }

                    //* 路径关闭
                    ctx.closePath();
                },
            });

            //* 绘制色块
            data.forEach((value, index) => {
                //* 数据初始化
                const offset = index == 0 ? 0 : minus ? 24 : 4;
                const extra = minus ? (index == 0 ? 24 : index == data.length - 1 ? 4 : 0) : index == 0 ? 4 : index == data.length - 1 ? 24 : 0;
                const color = minus ? (index % 2 == 0 ? "#E3E3E3" : "#858585") : index % 2 == (data.length - 1) % 2 ? "#E3E3E3" : "#858585";
                const start = (value.start - option.begin) * config.ratio;
                const size = value.size * config.ratio;

                //* 绘制内容块
                content.add(new Konva.Rect({ x: 0, y: start + offset, fill: color, width: 44, height: size + extra }));
            });

            //* 绘制内容
            result.add(content);

            //* 绘制线框
            result.add(
                new Konva.Rect({
                    x: config.offset + 1,
                    y: option.wireframe,
                    width: 46,
                    height: option.size * config.ratio + 30,
                    stroke: "#c7c7c7",
                    strokeWidth: 2,
                    cornerRadius: radius,
                })
            );
        }

        return result;
    };

    //* 标注绘制
    const emphasis = () => {
        //* 检测是否需要绘制
        if (!chromosome_note) return new Konva.Group();
        else {
            //* 配置项初始化
            const option = chromosome_note.id.startsWith("p") ? config_p : config_q;
            const offset = chromosome_note.id.startsWith("p") ? 4 : 24;
            //* 实例化结果
            const result = new Konva.Group();
            //* 数据初始化
            const start = (origin.chromosome.start - option.begin) * config.ratio + option.content + offset;
            const size = Math.max(origin.chromosome.size * config.ratio, 1);

            //* 绘制背景色块
            result.add(new Konva.Rect({ x: config.offset, y: start, width: 48, height: size, fill: "#72299d" }));
            result.add(new Konva.Path({ data: `M${config.offset},${start + size / 2} l-6,-6 l0,12 Z`, fill: "#72299d" }));
            result.add(note.x(0).y(start + size / 2 - 6));

            //* 绘制辅助线
            result.add(
                new Konva.Path({
                    data: `M${config.offset + 48},${start} L${config.offset + 208},1`,
                    strokeWidth: 1,
                    stroke: "#e3e3e3",
                })
            );

            //* 绘制辅助线
            result.add(
                new Konva.Path({
                    data: `M${config.offset + 48},${start + size} L${config.offset + 208},${setting.size}`,
                    strokeWidth: 1,
                    stroke: "#e3e3e3",
                })
            );

            return result;
        }
    };

    //* 绘制
    canvas.add(content(false, bands_p, config_p));
    canvas.add(content(true, bands_q, config_q));
    canvas.add(emphasis());

    //* 结果
    stage.add(canvas);
    stage.height(setting.size);
};
